import React from 'react';
import { Text } from 'components/Text';
import { TrackingLink } from 'components/TrackingLink';
import { ReturnStatus } from 'types/return';
import { formatStatusText, getStoreCurrencyFormatter } from 'utils';

const getReviewSignalClasses = (severity: string | undefined) => {
  switch (severity?.toUpperCase()) {
    case 'CRITICAL':
    case 'HIGH':
    case 'ERROR':
      return 'bg-red-100 text-red-700';
    case 'MEDIUM':
    case 'WARNING':
      return 'bg-yellow-100 text-yellow-700';
    case 'LOW':
    case 'INFO':
      return 'bg-blue-100 text-blue-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const getRuleFlagLabel = (flag: any) => {
  if (typeof flag === 'string') return formatStatusText(flag);
  return flag?.label || formatStatusText(flag?.code || '') || 'Flagged';
};

const getRuleFlagTitle = (flag: any) => {
  if (typeof flag === 'string') return formatStatusText(flag);
  return flag?.message || flag?.label || formatStatusText(flag?.code || '');
};

const RETURN_STATUS_LABELS: Partial<Record<ReturnStatus, string>> = {
  [ReturnStatus.SUBMITTED]: 'Return filed',
  [ReturnStatus.APPROVED]: 'Approved by merchant',
  [ReturnStatus.AWAITING_DROPOFF]: 'Awaiting customer shipment',
  [ReturnStatus.LABEL_READY]: 'Awaiting customer shipment',
  [ReturnStatus.IN_TRANSIT]: 'Return in transit',
  [ReturnStatus.RECEIVED]: 'Item received back at warehouse',
  [ReturnStatus.DENIED]: 'Denied by merchant',
  [ReturnStatus.RESOLVED]: 'Return resolved',
  [ReturnStatus.EXPIRED]: 'Return expired',
};

const getReturnStatusLabel = (status: ReturnStatus | string | undefined) =>
  RETURN_STATUS_LABELS[status as ReturnStatus] || formatStatusText(status);

export const columns = [
  {
    name: 'Updated',
    id: 1,
    render: (item: any) => (
      <Text
        value={
          item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : 'N/A'
        }
      />
    ),
    value: 'updatedAt',
  },
  {
    name: 'Return Status',
    id: 2,
    render: (item: any) => {
      let colors = 'bg-yellow-100 text-yellow-700'; // Default for SUBMITTED/others
      if (
        item.status === ReturnStatus.AWAITING_DROPOFF ||
        item.status === ReturnStatus.APPROVED ||
        item.status === ReturnStatus.RECEIVED
      )
        colors = 'bg-green-100 text-green-700';
      if (item.status === ReturnStatus.DENIED)
        colors = 'bg-red-100 text-red-700';
      if (item.status === ReturnStatus.EXPIRED)
        colors = 'bg-gray-100 text-gray-700';
      if (item.status === ReturnStatus.RESOLVED)
        colors = 'bg-blue-100 text-blue-700';
      if (
        item.status === ReturnStatus.LABEL_READY ||
        item.status === ReturnStatus.IN_TRANSIT
      ) {
        colors = 'bg-indigo-100 text-indigo-700';
      }

      return (
        <Text
          value={getReturnStatusLabel(item.status)}
          className={`${colors} px-3 py-1 rounded-full text-center`}
          type="xs"
        />
      );
    },
    value: 'status',
  },
  {
    name: 'Tracking',
    id: 'tracking',
    render: (item: any) =>
      item.trackingNumber?.trim() && item.id ? (
        <TrackingLink
          trackingNumber={item.trackingNumber}
          ownerType="RETURN"
          ownerId={item.id}
        />
      ) : (
        <Text value="N/A" />
      ),
    value: 'trackingNumber',
  },
  {
    name: 'Order Number',
    id: 3,
    render: (item: any) => {
      const orderNumber = item.orderNumber || item.order?.number || 'N/A';
      return item.orderId ? (
        <a href={`/orders/${item.orderId}`}>{orderNumber}</a>
      ) : (
        <Text value={orderNumber} />
      );
    },
    value: 'orderNumber',
  },
  {
    name: 'Customer Name',
    id: 4,
    render: (item: any) => (
      <Text
        value={`${item.customerName || item.order?.customerName || 'N/A'}`}
      />
    ),
    value: 'customerName',
  },
  {
    name: 'Customer Email',
    id: 5,
    render: (item: any) => (
      <Text
        value={`${item.customerEmail || item.order?.customerEmail || 'N/A'}`}
      />
    ),
  },
  {
    name: 'Order Total',
    id: 6,
    render: (item: any) => (
      <Text
        value={`${getStoreCurrencyFormatter(
          item.currencyCode || item.currency,
          item.total ?? item.order?.total,
        )}`}
      />
    ),
    value: 'total',
  },
  {
    name: 'Next Action',
    id: 7,
    render: (item: any) => (
      <Text
        value={formatStatusText(item.nextAction) || 'No action'}
        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-center"
        type="xs"
      />
    ),
    value: 'nextAction',
  },
  {
    name: 'Review & Risk',
    id: 8,
    render: (item: any) => {
      const reviewReasons = (item.reviewReasons || []).filter(Boolean);
      const ruleFlags = (item.ruleFlags || []).filter(Boolean);
      const riskLevel = item.riskLevel;
      const visibleReviewReasons = reviewReasons.slice(0, 2);
      const visibleRuleFlags = ruleFlags.slice(0, 2);
      const hiddenSignalCount =
        reviewReasons.length +
        ruleFlags.length -
        visibleReviewReasons.length -
        visibleRuleFlags.length;
      const hasRiskLevel = Boolean(riskLevel && riskLevel !== 'NONE');
      const hasReviewSignals = Boolean(
        item.reviewRequired ||
          hasRiskLevel ||
          reviewReasons.length ||
          ruleFlags.length,
      );

      if (!hasReviewSignals) {
        return <Text value="No review signals" className="text-gray-400" />;
      }

      return (
        <div className="flex max-w-sm flex-col gap-2">
          <div className="flex flex-wrap gap-1">
            {item.reviewRequired && (
              <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-700">
                Review required
              </span>
            )}
            {hasRiskLevel && (
              <span
                className={`${getReviewSignalClasses(
                  riskLevel,
                )} rounded-full px-2 py-1 text-xs font-medium`}
              >
                {formatStatusText(riskLevel)} risk
              </span>
            )}
          </div>
          {(reviewReasons.length || ruleFlags.length) && (
            <div className="flex flex-wrap gap-1">
              {visibleReviewReasons.map((reason: string) => (
                <span
                  key={reason}
                  className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600"
                >
                  {formatStatusText(reason)}
                </span>
              ))}
              {visibleRuleFlags.map((flag: any, index: number) => (
                <span
                  key={
                    typeof flag === 'string'
                      ? flag
                      : flag.code || flag.label || index
                  }
                  title={getRuleFlagTitle(flag)}
                  className={`${getReviewSignalClasses(
                    flag.severity || riskLevel,
                  )} rounded-full px-2 py-1 text-xs font-medium`}
                >
                  {getRuleFlagLabel(flag)}
                </span>
              ))}
              {hiddenSignalCount > 0 && (
                <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                  +{hiddenSignalCount}
                </span>
              )}
            </div>
          )}
        </div>
      );
    },
  },
];
